import React, { useState, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stack,
    Button,
    alpha
} from '@mui/material';
import { Print as PrintIcon, FileDownload as DownloadIcon } from '@mui/icons-material';
import { formatCurrency } from '../../../utils/compteClientEngine';
import { COLORS, jsonToXlsx } from '../../../settings/themeSettings';

const ExtraitCompteViewer = ({ data }) => {
    const [activeRowIdx, setActiveRowIdx] = useState(0);
    const tableRef = useRef(null);

    if (!data) return null;

    const {
        clientName = 'Client',
        startDateStr = 'Origine',
        endDateStr = 'Ce jour',
        rows = [],
        totalDebit = 0,
        totalCredit = 0,
        soldeFinal = 0
    } = data;

    const dateTirage = new Date().toLocaleDateString('fr-FR') + ' à ' + new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

    const handleKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveRowIdx(prev => Math.min(rows.length - 1, prev + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveRowIdx(prev => Math.max(0, prev - 1));
        }
    };

    const handleExportExcel = () => {
        const exportData = rows.map(r => ({
            'Date': r.dateStr,
            'Pièce': r.piece,
            'Libellé': r.libelle,
            'Débit (Facture CFA)': r.debit,
            'Crédit (Encaissement CFA)': r.credit,
            'Solde Cumulé (CFA)': r.soldeCumule
        }));
        jsonToXlsx(exportData, `Extrait_Compte_${clientName.replace(/\s+/g, '_')}`);
    };

    const handlePrint = () => {
        window.print();
    };

    return (
        <Box tabIndex={0} ref={tableRef} onKeyDown={handleKeyDown} sx={{ outline: 'none' }}>
            {/* TOOLBAR EXPORT */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {rows.length} mouvement{rows.length > 1 ? 's' : ''} enregistré{rows.length > 1 ? 's' : ''}
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Imprimer
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Export Excel
                    </Button>
                </Stack>
            </Stack>

            {/* DOCUMENT OFFICIEL IMPRIMABLE (SECTION 5.3) */}
            <Paper
                elevation={0}
                className="printable-document"
                sx={{
                    p: 3,
                    border: '2px solid #0f172a',
                    borderRadius: '8px',
                    bgcolor: 'white',
                    maxWidth: '1000px',
                    mx: 'auto'
                }}
            >
                {/* EN-TÊTE TRANSVERSAL COMMUN */}
                <Box sx={{ borderBottom: '2px solid #0f172a', pb: 1.5, mb: 2 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.2fr 2fr 1.2fr', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                                AFRICA FOR TOURISM
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                Agence de Voyages & Tourisme
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                TravelManager v2.0
                            </Typography>
                        </Box>

                        <Box textAlign="center">
                            <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 1, color: '#0f172a' }}>
                                Extrait de compte
                            </Typography>
                        </Box>

                        <Box textAlign="right">
                            <Typography variant="caption" display="block">
                                <strong>Période du :</strong> {startDateStr} <strong>Au :</strong> {endDateStr}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Date de tirage : {dateTirage}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                                Page : 1 / 1
                            </Typography>
                        </Box>
                    </Box>

                    {/* BANDEAU CLIENT */}
                    <Box sx={{ mt: 1.5, p: 1, bgcolor: '#0f172a', color: 'white', borderRadius: '4px', fontWeight: 'bold' }}>
                        Client : {clientName}
                    </Box>
                </Box>

                {/* TABLEAU DES ÉCRITURES CHRONOLOGIQUES */}
                <Box sx={{ border: '1px solid #0f172a', borderRadius: '4px', overflow: 'hidden' }}>
                    {/* Titres de colonnes figés */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '110px 140px 1fr 140px 140px 150px', bgcolor: '#0f172a', color: 'white', p: 1, fontWeight: 'bold', fontSize: '0.85rem' }}>
                        <Box>Date</Box>
                        <Box>Pièce</Box>
                        <Box>Libellé</Box>
                        <Box sx={{ textAlign: 'right' }}>Débit (facture)</Box>
                        <Box sx={{ textAlign: 'right' }}>Crédit (encaissement)</Box>
                        <Box sx={{ textAlign: 'right' }}>Solde cumulé</Box>
                    </Box>

                    <Box sx={{ maxHeight: '55vh', overflowY: 'auto' }}>
                        {rows.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                Aucun mouvement enregistré pour ce client sur la période sélectionnée.
                            </Box>
                        ) : (
                            rows.map((r, idx) => {
                                const isSelected = activeRowIdx === idx;
                                return (
                                    <Box
                                        key={r.id || idx}
                                        onClick={() => setActiveRowIdx(idx)}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '110px 140px 1fr 140px 140px 150px',
                                            p: 1,
                                            alignItems: 'center',
                                            fontSize: '0.85rem',
                                            borderBottom: '1px solid #e2e8f0',
                                            bgcolor: isSelected ? alpha(COLORS.primary, 0.12) : (idx % 2 === 0 ? 'white' : '#f8fafc'),
                                            borderLeft: isSelected ? `4px solid ${COLORS.primary}` : '4px solid transparent',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <Box>{r.dateStr}</Box>
                                        <Box sx={{ fontWeight: 600, color: COLORS.primary }}>{r.piece}</Box>
                                        <Box sx={{ color: 'text.secondary' }}>{r.libelle}</Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: r.debit > 0 ? 700 : 400 }}>
                                            {r.debit > 0 ? formatCurrency(r.debit) : '-'}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: r.credit > 0 ? 700 : 400, color: r.credit > 0 ? '#16a34a' : 'inherit' }}>
                                            {r.credit > 0 ? formatCurrency(r.credit) : '-'}
                                        </Box>
                                        <Box sx={{ textAlign: 'right', fontWeight: 800, color: r.soldeCumule > 0 ? '#dc2626' : (r.soldeCumule < 0 ? '#2563eb' : '#0f172a') }}>
                                            {formatCurrency(r.soldeCumule)}
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>

                    {/* LIGNE DES TOTAUX (SECTION 5.3) */}
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: '110px 140px 1fr 140px 140px 150px',
                            bgcolor: '#f1f5f9',
                            p: 1.2,
                            fontWeight: 'bold',
                            fontSize: '0.875rem',
                            borderTop: '2px solid #0f172a'
                        }}
                    >
                        <Box sx={{ gridColumn: 'span 3', textAlign: 'right', pr: 2 }}>
                            Totaux :
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#0f172a' }}>
                            {formatCurrency(totalDebit)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: '#16a34a' }}>
                            {formatCurrency(totalCredit)}
                        </Box>
                        <Box sx={{ textAlign: 'right', color: soldeFinal > 0 ? '#dc2626' : '#0f172a', fontWeight: 900 }}>
                            {formatCurrency(soldeFinal)} CFA
                        </Box>
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ExtraitCompteViewer;
